import { memo, useCallback } from 'react';
import {
  BottomSheet,
  useNavigation,
  StackNavigationProp,
  navigationRef,
} from '@betty/navigation';
import { useSelector } from '@betty/toolkit/state';
import { logError } from '@betty/toolkit/logging';
import { selectCashBalance } from '../../state/cashBalance/cashBalance.slice';
import {
  handleRestrictedFunctionality,
  retrieveTransactionIdFromError,
} from '../../utils/payments';
import { ERROR_CONTEXT } from './WithdrawInterac.content';
import { WithdrawInteracProps } from './WithdrawInterac.types';
import { DepositNavigatorParamsList } from '../../navigators/DepositNavigator/DepositNavigator.types';
import { PaymentHandler } from '../../components/PaymentInterac/PaymentInterac.types';
import PaymentInterac from '../../components/PaymentInterac/PaymentInterac';
import PaymentService from '../../services/PaymentService/PaymentService';
import PaymentLaunchService from '../../services/PaymentLaunchService/PaymentLaunchService';

function WithdrawInterac({
  minAmount = 0,
  maxAmount,
  close,
}: WithdrawInteracProps) {
  const navigation =
    useNavigation<StackNavigationProp<DepositNavigatorParamsList>>();
  const cashBalance = useSelector(selectCashBalance);

  const getEmailStatus = useCallback(async (email: string) => {
    try {
      const { status } = await PaymentService.checkInteracEmail({
        data: { email },
      });

      return status;
    } catch (error) {
      logError('Check Interac email request failure.', error);
    }
  }, []);

  const onPayment = useCallback<PaymentHandler>(
    async (data) => {
      try {
        const { email } = data;
        const status = await getEmailStatus(email);

        if (!status) {
          return;
        }

        if (status !== 'Verified') {
          close();

          BottomSheet.open({
            namespace: 'wallet',
            type: 'WithdrawEmailStatus',
            data: {
              email,
              status,
              onLinkSent: () => {
                BottomSheet.open({
                  namespace: 'common',
                  type: 'InfoImage',
                  data: {
                    image: 'common.success',
                    titleNS: 'wallet',
                    titleKey: 'interacWithdrawEmailSentTitle',
                    descriptionNS: 'wallet',
                    descriptionKey: 'interacWithdrawEmailSentDescription',
                  },
                });
              },
            },
          });

          return;
        }

        const { transactionId } = await PaymentLaunchService.withdrawalInterac({
          data,
        });

        close();

        navigationRef.navigate('PaymentStatusScreen', {
          transactionId: `${transactionId}`,
        });
      } catch (error) {
        logError('Launch Interac withdraw request failure.', error);

        close();

        const transactionId = retrieveTransactionIdFromError(error);

        if (transactionId !== undefined) {
          navigation.navigate('PaymentStatusScreen', {
            transactionId: `${transactionId}`,
          });

          return;
        }

        handleRestrictedFunctionality(error);
      }
    },
    [getEmailStatus, navigation, close],
  );

  return (
    <PaymentInterac
      amount={minAmount}
      minAmount={minAmount}
      maxAmount={maxAmount ?? cashBalance}
      errorContext={ERROR_CONTEXT}
      onPayment={onPayment}
      showAmountField
      buttonKey="interacWithdraw"
      footerKey="interacWithdrawFooter"
      paddingTop="space-400"
    />
  );
}

export default memo(WithdrawInterac);
